import React from 'react';

interface NovaLogoIconProps {
  thinking?: boolean;
  className?: string;
}

export const NovaLogoIcon: React.FC<NovaLogoIconProps> = ({
  thinking = false,
  className = 'w-8 h-8',
}) => {
  return (
    <div
      className={`relative inline-flex items-center justify-center select-none ${className}`}
      style={{
        animation: thinking ? 'vexlLogoPulse 2s ease-in-out infinite' : 'none',
      }}
    >
      <style>{`
        @keyframes vexlLogoPulse {
          0%, 100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-2px) scale(1.04);
          }
        }
        @keyframes vexlAuraGlow {
          0%, 100% {
            transform: scale(0.92);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.18);
            opacity: 0.42;
          }
        }
        @keyframes vexlWave1 {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes vexlWave2 {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes vexlWave3 {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes vexlWave4 {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* 🌟 Soft Ambient Aura when thinking */}
      {thinking && (
        <div
          className="absolute -inset-1.5 rounded-full bg-gradient-to-tr from-[#6830FF]/35 via-[#BE82FF]/20 to-[#440AD6]/35 blur-md pointer-events-none"
          style={{
            animation: 'vexlAuraGlow 2.2s ease-in-out infinite',
          }}
        />
      )}

      <svg
        width="100%"
        height="100%"
        viewBox="0 0 736 614"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="relative z-10 overflow-visible"
        style={{
          filter: thinking ? 'drop-shadow(0 0 6px rgba(127,119,221,0.35))' : 'none',
          transition: 'filter 0.4s ease',
        }}
      >
        {/* Wave 1 (Bottom) */}
        <path
          d="M130.5 609.5L13.5 611.5C40.1667 562.167 93.7 462.8 94.5 460C104.5 459 167.5 429.5 173 426C237.5 399 265.5 423 277.5 429.5C291.5 441.1 308 461.667 314.5 470.5C289 444 271.5 468.5 265.5 475.5C260 477.5 233 530.5 211 558.883C188.2 595.59 147.833 607.922 130.5 609.5Z"
          fill="url(#paint0_linear_1_7)"
          style={{
            animation: thinking ? 'vexlWave1 1.8s ease-in-out infinite 0s' : 'none',
          }}
        />
        {/* Wave 2 */}
        <path
          d="M72 465.5C51.6 472.3 15.5 469.667 0 467.5C79 431 94.5 388 98.5 381.5C111 374.5 151 359.5 159 354C239.5 318 284.5 354 293 359.5C314 365 386 450.5 392.407 453.737C422.814 485.475 466 496.493 478.5 495.312C564.1 498.762 625.5 386.542 645.5 330C628.167 380.333 592.314 484.099 587.571 496.493C534.428 661.687 436.381 616.328 394 573C366.5 537.5 311.237 466.072 310.184 464.358C277.368 422.217 262 420.5 257.674 418.33C214.849 401.66 164 429.5 159 432C155 434 121 452 94.5 460C68 468 76 464.7 72 465.5Z"
          fill="url(#paint1_linear_1_7)"
          style={{
            animation: thinking ? 'vexlWave2 1.8s ease-in-out infinite 0.25s' : 'none',
          }}
        />
        {/* Wave 3 */}
        <path
          d="M99.5 381C64.3 395 27.9842 391.383 14.9842 387.717C78.4683 364.433 114.5 299.5 116.634 295.15C124.268 290.799 145.5 280.5 152.5 276C190.5 252.5 219.5 248.5 232.5 245.5C290.5 235 346.5 289 351.5 293C382.5 327.5 397 339.704 405 347C502.2 416.2 568.5 329.5 589.5 277.5C630 185.333 711.2 0.8 712 0C724.4 157.2 732.833 226.5 735.5 241.5L686 207C674.667 242.833 651 317 647 327C639.5 346 615 391.5 612.015 396.029C597.529 425.49 564.5 458 560.5 461C471.5 542 390 452.5 381 443.5C338.5 395.5 317 377.5 305.743 366.568C261.985 330.636 213.5 339.5 205.391 339.704C160.782 349.908 107.5 377.5 99.5 381Z"
          fill="url(#paint2_linear_1_7)"
          style={{
            animation: thinking ? 'vexlWave3 1.8s ease-in-out infinite 0.5s' : 'none',
          }}
        />
        {/* Wave 4 (Top Arrow) */}
        <path
          d="M118 293.559C82.4 312.806 46.8333 308.539 33.5 304C111 265.5 155.5 201 160.5 194.5C187 155 201.5 143 206 139.5C276.5 88 328.5 132 340.5 143C362 151.5 415 234 422 242C475 312.5 507 253.924 509.644 248.187C514.559 243.597 547.263 176.15 563 143L485.5 122.5L711.5 1L672 85C647.667 142.5 597.633 260.53 592.166 272.65C575.333 308.3 558.5 328 554.5 333C465.5 430.5 378 321.5 373.523 316.863C356.046 296.226 334.5 279 328.5 274C261.5 223 201.5 253.5 196.716 253.924C186.432 257.849 120 292.5 118 293.559Z"
          fill="url(#paint3_linear_1_7)"
          style={{
            animation: thinking ? 'vexlWave4 1.8s ease-in-out infinite 0.75s' : 'none',
          }}
        />


        <defs>
          <linearGradient
            id="paint0_linear_1_7"
            x1="164"
            y1="413.453"
            x2="164"
            y2="611.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2C00B1" />
            <stop offset="1" stopColor="#963EFF" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_1_7"
            x1="312.5"
            y1="330"
            x2="263.5"
            y2="532"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#3F06D3" />
            <stop offset="0.439816" stopColor="#722DE8" />
            <stop offset="1" stopColor="#BE82FF" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_1_7"
            x1="375.242"
            y1="-1.44451e-05"
            x2="708"
            y2="381"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.0305383" stopColor="#440AD6" />
            <stop offset="0.485577" stopColor="#6830FF" />
            <stop offset="0.873027" stopColor="#7F77DD" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_1_7"
            x1="273.75"
            y1="299.75"
            x2="584.75"
            y2="158.25"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#D6AEFF" />
            <stop offset="0.509135" stopColor="#A977F4" />
            <stop offset="1" stopColor="#7F77DD" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default NovaLogoIcon;


